import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@Sage/hoi-poi-ui';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { getNumberAsLocaleNumber } from 'utils/numbers';

import CampaignSection from './CampaignSection';
import SenderForm from './SenderForm';
import RecipientsForm from './RecipientsForm';
import SubjectForm from './SubjectForm';
import DesignForm from './DesignForm';

const CampaignDetailsFormContent = memo(
    ({
        completedSections,
        expandedSections,
        hasExpandedSectionForDimming,
        recipientsCount = 0,
        senderDisplayName,
        senderEmail,
        senders,
        selectedSenderId,
        subject,
        previewText,
        theme,
        onAddRecipients,
        onCancelRecipients,
        onDesignOptionSelect,
        onOpenSenderModal,
        onPreviewTextChange,
        onSaveRecipients,
        onSaveSender,
        onSaveSubject,
        onSelectedSenderChange,
        onSubjectChange,
        onToggleSection,
        senderSelectInstanceKey = 0,
        campaignHtml = '',
        onResetDesign,
    }) => (
        <>
            <CampaignSection
                isCompleted={completedSections.sender}
                title={getLiteral('label_sender')}
                subtitle={
                    selectedSenderId && senderEmail && !expandedSections.sender ? (
                        <div className="campaign-details__section-subtitle">
                            <Text type="captionMedium">{senderDisplayName}</Text>
                            <Text type="caption" color={theme.colors.textLight.secondary}>
                                {`• ${senderEmail}`}
                            </Text>
                        </div>
                    ) : (
                        getLiteral('label_marketing_campaigns_who_is_sending_this_email_campaign')
                    )
                }
                actionLabel={getLiteral('action_manage_sender')}
                isExpanded={expandedSections.sender}
                onToggle={() => onToggleSection('sender')}
                isDimmed={hasExpandedSectionForDimming && !expandedSections.sender}
            >
                <SenderForm
                    selectInstanceKey={senderSelectInstanceKey}
                    senders={senders}
                    selectedSenderId={selectedSenderId}
                    senderDisplayName={senderDisplayName}
                    onSave={onSaveSender}
                    onCancel={() => onToggleSection('sender')}
                    onAddNewSender={onOpenSenderModal}
                    onSelectedSenderChange={onSelectedSenderChange}
                />
            </CampaignSection>

            <CampaignSection
                isCompleted={completedSections.recipients}
                title={getLiteral('label_recipients')}
                subtitle={
                    recipientsCount > 0 && !expandedSections.recipients
                        ? `${getNumberAsLocaleNumber(recipientsCount)} ${getLiteral('label_recipients_selected')}`
                        : getLiteral('label_marketing_campaigns_recipients_campaign_desc')
                }
                actionLabel={getLiteral(
                    recipientsCount > 0 ? 'action_manage_recipients' : 'action_add_recipients',
                )}
                isExpanded={expandedSections.recipients}
                onToggle={() => onToggleSection('recipients')}
                isDimmed={hasExpandedSectionForDimming && !expandedSections.recipients}
            >
                <RecipientsForm
                    selectedRecipients={recipientsCount}
                    onAddRecipients={onAddRecipients}
                    onCancel={onCancelRecipients}
                    onSave={onSaveRecipients}
                />
            </CampaignSection>

            <CampaignSection
                isCompleted={completedSections.subject}
                title={getLiteral('label_subject')}
                subtitle={
                    subject?.trim() && !expandedSections.subject ? (
                        <div className="campaign-details__section-subtitle">
                            <Text type="captionMedium" isTruncated useTooltip>
                                {subject.trim()}
                            </Text>
                        </div>
                    ) : (
                        getLiteral('label_marketing_campaigns_subject_campaign_desc')
                    )
                }
                actionLabel={getLiteral(
                    completedSections.subject ? 'action_manage_subject' : 'action_add_subject',
                )}
                isExpanded={expandedSections.subject}
                onToggle={() => onToggleSection('subject')}
                isDimmed={hasExpandedSectionForDimming && !expandedSections.subject}
            >
                <SubjectForm
                    subject={subject}
                    previewText={previewText}
                    senderDisplayName={senderDisplayName}
                    onSubjectChange={onSubjectChange}
                    onPreviewTextChange={onPreviewTextChange}
                    onCancel={() => onToggleSection('subject')}
                    onSave={onSaveSubject}
                />
            </CampaignSection>

            <CampaignSection
                isCompleted={completedSections.design}
                title={getLiteral('label_design')}
                subtitle={getLiteral('label_marketing_campaigns_design_campaign_desc')}
                actionLabel={
                    completedSections.design
                        ? getLiteral('action_edit_design')
                        : getLiteral('action_start_designing')
                }
                expandedActionButton={completedSections.design}
                onExpandedActionClick={() => onDesignOptionSelect({ id: 'html_editor' })}
                onExpandedResetClick={
                    completedSections.design && onResetDesign ? onResetDesign : undefined
                }
                expandedResetTooltip={
                    completedSections.design ? getLiteral('action_reset') : undefined
                }
                isExpanded={expandedSections.design || completedSections.design}
                onToggle={() => onToggleSection('design')}
                isDimmed={Boolean(
                    expandedSections.sender ||
                    expandedSections.recipients ||
                    expandedSections.subject ||
                    expandedSections.additionalSettings,
                )}
            >
                <DesignForm html={campaignHtml} onSelectOption={onDesignOptionSelect} />
            </CampaignSection>

            {/* <CampaignSection
                isCompleted={completedSections.additionalSettings}
                title={getLiteral('label_additional_settings')}
                subtitle=""
                actionLabel={getLiteral('action_modify_settings')}
                isExpanded={expandedSections.additionalSettings}
                onToggle={() => onToggleSection('additionalSettings')}
                hideStatusIcon
                isDimmed={hasExpandedSection && !expandedSections.additionalSettings}
            ></CampaignSection> */}
        </>
    ),
);

CampaignDetailsFormContent.displayName = 'CampaignDetailsFormContent';

CampaignDetailsFormContent.propTypes = {
    completedSections: PropTypes.shape({
        sender: PropTypes.bool,
        recipients: PropTypes.bool,
        subject: PropTypes.bool,
        design: PropTypes.bool,
        additionalSettings: PropTypes.bool,
    }).isRequired,
    expandedSections: PropTypes.shape({
        sender: PropTypes.bool,
        recipients: PropTypes.bool,
        subject: PropTypes.bool,
        design: PropTypes.bool,
        additionalSettings: PropTypes.bool,
    }).isRequired,
    hasExpandedSectionForDimming: PropTypes.bool.isRequired,
    recipientsCount: PropTypes.number,
    senderDisplayName: PropTypes.string,
    senderEmail: PropTypes.string,
    senders: PropTypes.array.isRequired,
    selectedSenderId: PropTypes.string,
    subject: PropTypes.string,
    previewText: PropTypes.string,
    theme: PropTypes.object.isRequired,
    onAddRecipients: PropTypes.func.isRequired,
    onCancelRecipients: PropTypes.func.isRequired,
    onDesignOptionSelect: PropTypes.func.isRequired,
    onOpenSenderModal: PropTypes.func.isRequired,
    onPreviewTextChange: PropTypes.func.isRequired,
    onSaveRecipients: PropTypes.func.isRequired,
    onSaveSender: PropTypes.func.isRequired,
    onSaveSubject: PropTypes.func.isRequired,
    onSelectedSenderChange: PropTypes.func.isRequired,
    onSubjectChange: PropTypes.func.isRequired,
    onToggleSection: PropTypes.func.isRequired,
    senderSelectInstanceKey: PropTypes.number,
    campaignHtml: PropTypes.string,
    onResetDesign: PropTypes.func,
};

export default CampaignDetailsFormContent;
